import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import type { FormValueControl } from '@angular/forms/signals';
import { UiFieldControl } from '../../../basics/ui-field-control';
import { RadioGroupDirection, RadioGroupDirectionType } from '../../../types';
import { getRandomId } from '../../../utils/get-random-id';

/** The least space between options laid out in `columns`. */
const COLUMN_GAP_PX = 15;

/** Owns the value; each projected `fr8bot-ui-radio-option` reads and writes it through DI. */
@Component({
  selector: 'fr8bot-ui-radio-group-field',
  template: `
    <!-- Horizontal options wrap rather than squeeze their labels onto two lines. -->
    <div role="radiogroup" [class]="layoutClass()" [style.column-gap.px]="columnGap()">
      <ng-content />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadioGroupFieldComponent<ValueType>
  extends UiFieldControl
  implements FormValueControl<ValueType | null>
{
  readonly value = model<ValueType | null>(null);
  readonly direction = input<RadioGroupDirectionType>(RadioGroupDirection.Horizontal);
  /**
   * Gives each horizontal option an equal share of the group's width, so groups of the same width
   * line up under each other even with fewer options. A share shrinks with the group down to its
   * option's content, keeping at least 15px between options; past that the options wrap.
   */
  readonly columns = input<number | null>(null);
  /** Marks the options red when the group's own errors are not what makes it invalid (a row-level rule). */
  readonly isInvalid = input(false);

  readonly isInvalidShown = computed(() => this.isInvalid() || this.isErrorShown());

  private readonly isColumns = computed(
    () => !!this.columns() && this.direction() === RadioGroupDirection.Horizontal,
  );

  protected readonly layoutClass = computed(() => {
    if (this.direction() === RadioGroupDirection.Vertical) {
      return 'tw:flex tw:flex-col';
    }

    return this.isColumns()
      ? 'tw:flex tw:flex-wrap tw:gap-y-2.5'
      : 'tw:flex tw:flex-wrap tw:gap-x-5 tw:gap-y-2';
  });

  protected readonly columnGap = computed(() => (this.isColumns() ? COLUMN_GAP_PX : null));

  /** Each option's `flex` in `columns`; `null` lets options size to their content. */
  readonly optionFlex = computed(() => {
    const columns = this.columns();

    return columns && this.isColumns()
      ? `0 0 calc((100% - ${(columns - 1) * COLUMN_GAP_PX}px) / ${columns})`
      : null;
  });

  // Not `name`: `[formField]` owns that input name.
  readonly groupName = getRandomId('radio-group');

  select(value: ValueType | null): void {
    this.value.set(value);
    this.touch.emit();
  }
}
